// FILE: components/sections/FormValidationSection.tsx
import React, { useState, useMemo } from "react";
import { Icon } from "../icons";
import { SectionContainer } from "../Content";
import { Breadcrumb } from "../Breadcrumb";
import { PasswordInput } from "../PasswordInput";

interface FormValidationSectionProps {
  groupTitle: string;
  sectionTitle: string;
  id: string;
}

export const FormValidationSection: React.FC<FormValidationSectionProps> = ({ 
  groupTitle, 
  sectionTitle, 
  id 
}) => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // Real-time validation
  const validations = useMemo(() => {
    return {
      length: password.length >= 8,
      upper: /[A-Z]/.test(password),
      lower: /[a-z]/.test(password),
      number: /\d/.test(password),
      special: /[@$!%*?&]/.test(password),
      match: password !== "" && password === confirmPassword,
    };
  }, [password, confirmPassword]);

  const isValid = Object.values(validations).every(Boolean);

  return (
    <SectionContainer id={id}>
      <Breadcrumb group={groupTitle} section={sectionTitle} />
      <h2 className="section-title">Form Validation</h2>
      <p className="section-subtitle">
        Real-time form validation with visual feedback. This example demonstrates password validation with multiple criteria.
      </p>

      <div className="component-preview">
        <form
          className="w-full max-w-md mx-auto p-6 bg-white dark:bg-dark-bg-primary rounded-2xl shadow-lg space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (isValid) {
              alert("Password set successfully ✅");
            }
          }}
        >
          <h3 className="text-xl font-semibold text-center mb-4">Set Your Password</h3>

          {/* Password Input */}
          <div>
            <PasswordInput
              id="password"
              placeholder="Enter new password"
              value={password}
              onChange={setPassword}
              autoComplete="new-password"
            />
          </div>

          {/* Confirm Password Input */}
          <div>
            <PasswordInput
              id="confirmPassword"
              placeholder="Confirm password"
              value={confirmPassword}
              onChange={setConfirmPassword}
              autoComplete="new-password"
            />
          </div>

          {/* Validation Checklist */}
          <ul className="text-sm space-y-1">
            <li className={validations.length ? "text-system-success" : "text-system-error"}>
                <Icon className="!text-sm mr-2">{validations.length ? 'check_circle' : 'cancel'}</Icon>
                Minimum 8 characters
            </li>
            <li className={validations.upper ? "text-system-success" : "text-system-error"}>
                <Icon className="!text-sm mr-2">{validations.upper ? 'check_circle' : 'cancel'}</Icon>
                One uppercase letter
            </li>
            <li className={validations.lower ? "text-system-success" : "text-system-error"}>
                <Icon className="!text-sm mr-2">{validations.lower ? 'check_circle' : 'cancel'}</Icon>
                One lowercase letter
            </li>
            <li className={validations.number ? "text-system-success" : "text-system-error"}>
                <Icon className="!text-sm mr-2">{validations.number ? 'check_circle' : 'cancel'}</Icon>
                One number
            </li>
            <li className={validations.special ? "text-system-success" : "text-system-error"}>
                <Icon className="!text-sm mr-2">{validations.special ? 'check_circle' : 'cancel'}</Icon>
                One special character
            </li>
            <li className={validations.match ? "text-system-success" : "text-system-error"}>
                <Icon className="!text-sm mr-2">{validations.match ? 'check_circle' : 'cancel'}</Icon>
                Passwords match
            </li>
          </ul>

          <button
            type="submit"
            disabled={!isValid}
            className="w-full h-14 bg-brand-orange hover:bg-brand-orange/90 disabled:bg-text-secondary/50 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors"
          >
            Set Password
          </button>
        </form>
      </div>
    </SectionContainer>
  );
};

